import React, { useEffect, useMemo, useState } from "react";
import SearchBar from "@/components/SearchBar";
import Segmented from "@/components/Segmented";
import { Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { HeaderFilter } from "./TicketsRegister";
import DeliveryPanel from "./DeliveryPanel";

const MODES = [["all", "All"], ["Ready", "Ready"], ["Delivered", "Delivered"], ["Issues", "Issues"]];   // All left, Ready is always the default
const STATUS_TONE = { Ready: "bg-indigo-100 text-indigo-900 dark:bg-indigo-900/40 dark:text-indigo-200", Delivered: "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200", Issues: "bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-200" };
const TYPE_TONE = { bulk: "bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200", spread: "bg-violet-100 text-violet-900 dark:bg-violet-900/40 dark:text-violet-200" };
const fmtDay = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: "numeric", day: "numeric" }) : "—");
const COLS = 8;

function Row({ r, selected, onOpen }) {
  const cell = "px-2 py-1.5 text-xs text-foreground whitespace-nowrap";
  return (
    <tr onClick={() => onOpen(r.id)} data-testid={`dlv-row-${r.ticket_number}`} data-selected={selected ? "1" : "0"}
        className={`cursor-pointer border-b border-border ${selected ? "bg-[#007BFF]/10" : "hover:bg-muted/50"}`}>
      <td className={`${cell} font-mono font-semibold`}>{r.job_number || "—"}</td>
      <td className={`${cell} max-w-[16rem] truncate`}>{r.project_name}<span className="ml-1.5 font-mono text-[10px] text-muted-foreground">{r.ticket_number}</span></td>
      <td className={`${cell} max-w-[14rem] truncate`} title={r.address}>{r.address || "—"}</td>
      <td className={cell} data-testid={`dlv-contact-${r.ticket_number}`}>{r.site_contact || "—"}</td>
      <td className={cell}><span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${TYPE_TONE[r.delivery_type]}`} data-testid={`dlv-type-${r.ticket_number}`}>{r.delivery_type === "bulk" ? "Bulk" : "Spread"}</span></td>
      <td className={cell} data-testid={`dlv-location-${r.ticket_number}`}>
        {r.location?.level ? <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 text-muted-foreground" />{r.location.level}{r.location.label && r.location.label !== "Rooms" ? <span className="text-muted-foreground">· {r.location.label}</span> : null}</span> : "—"}
      </td>
      <td className={`${cell} font-semibold`}>{fmtDay(r.deliver_by)}</td>
      <td className={cell}>
        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_TONE[r.board_status]}`} data-testid={`dlv-status-${r.ticket_number}`}>{r.board_status}</span>
        {r.missing ? <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-600 text-white" data-testid={`dlv-missing-${r.ticket_number}`}>{r.missing} missing</span> : null}
        {r.board_status !== "Delivered" && r.released > 0 ? <span className="ml-1 text-[10px] text-muted-foreground tabular-nums">{r.pct}%</span> : null}
      </td>
    </tr>
  );
}

/** Warehouse › Deliveries — ONE grid like Tickets (Ready · Delivered · Issues · All); a row opens the delivery panel on the right (desktop) / full screen (phone). */
export default function DeliveriesTab({ refreshKey = 0, dlvId = "", wide, actor, onOpen }) {
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const [mode, setMode] = useState("Ready");
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState([]);
  const [statusF, setStatusF] = useState([]);
  const [contactF, setContactF] = useState([]);
  const [group, setGroup] = useState("");
  useEffect(() => {
    let live = true;
    api.get("/delivery/board").then(({ data: d }) => { if (live) setData(d); }).catch((e) => toast.error(e.response?.data?.detail?.message || "Could not load deliveries"));
    return () => { live = false; };
  }, [refreshKey, tick]);
  const rows = useMemo(() => {
    if (!data) return [];
    const n = q.trim().toLowerCase();
    return data.rows.filter((r) => (mode === "all" || r.board_status === mode || r.id === dlvId)
      && (typeF.length === 0 || typeF.includes(r.delivery_type)) && (statusF.length === 0 || statusF.includes(r.board_status)) && (contactF.length === 0 || contactF.includes(r.site_contact))
      && (!n || `${r.job_number} ${r.project_name} ${r.ticket_number} ${r.address} ${r.site_contact}`.toLowerCase().includes(n)));
  }, [data, mode, q, typeF, statusF, contactF, dlvId]);
  if (!data) return <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  const selected = data.rows.find((r) => r.id === dlvId);
  const th = "px-2 py-1 font-medium";
  const toggleGroup = (k) => () => setGroup(group === k ? "" : k);
  const groups = (() => {
    if (!group) return [{ key: "", label: "", rows }];
    const m = new Map();
    rows.forEach((r) => { const k = group === "delivery_type" ? (r.delivery_type === "bulk" ? "Bulk" : "Spread") : r.board_status; if (!m.has(k)) m.set(k, { key: k, label: k, rows: [] }); m.get(k).rows.push(r); });
    return [...m.values()].sort((a, b) => a.key.localeCompare(b.key));
  })();
  const table = (
    <div className="rounded-lg border border-border bg-card overflow-x-auto" data-testid="deliveries-grid" data-mode={mode} data-group={group}>
      <table className="w-full">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border bg-muted/40">
            <th className={th}>Job #</th><th className={th}>Name</th><th className={th}>Address</th>
            <HeaderFilter label="Site contact" options={data.filters.foremen.map((n) => ({ key: n, label: n }))} value={contactF} onChange={setContactF} testId="deliveries-contact" />
            <HeaderFilter label="Type" options={[{ key: "bulk", label: "Bulk" }, { key: "spread", label: "Spread" }]} value={typeF} onChange={setTypeF} testId="deliveries-type" grouped={group === "delivery_type"} onGroup={toggleGroup("delivery_type")} />
            <th className={th}>Location</th><th className={th}>Deliver by</th>
            <HeaderFilter label="Status" options={data.statuses.map((k) => ({ key: k, label: k }))} value={statusF} onChange={setStatusF} testId="deliveries-status" grouped={group === "board_status"} onGroup={toggleGroup("board_status")} />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={COLS} className="p-6 text-center text-sm text-muted-foreground" data-testid="deliveries-empty">No deliveries here.</td></tr>}
          {groups.map((g) => (
            <React.Fragment key={g.key || "all"}>
              {group && <tr className="bg-muted/40 border-b border-border" data-testid={`deliveries-group-${g.key}`}><td colSpan={COLS} className="px-3 py-1.5 text-xs font-semibold text-foreground">{g.label} <span className="text-muted-foreground font-normal">· {g.rows.length}</span></td></tr>}
              {g.rows.map((r) => <Row key={r.id} r={r} selected={r.id === dlvId} onOpen={onOpen} />)}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
  const panel = selected || dlvId ? (
    <DeliveryPanel key={dlvId} ticketId={dlvId} row={selected} actor={actor} wide={wide} onClose={() => onOpen("")} onChanged={() => setTick((n) => n + 1)} />
  ) : null;
  if (!wide && dlvId) return panel;
  return (
    <div className="space-y-4" data-testid="deliveries-tab">
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="deliveries-strip">
        <SearchBar value={q} onChange={setQ} placeholder="Search job, ticket, address…" testId="deliveries-search" width="w-56" />
        <Segmented options={MODES.map(([k, l]) => ({ key: k, label: l, testId: `deliveries-mode-${k.toLowerCase()}` }))} value={mode} onChange={setMode} />
        <span className="ml-auto text-xs text-muted-foreground" data-testid="deliveries-count">{rows.length} deliver{rows.length === 1 ? "y" : "ies"}</span>
      </div>
      {wide && dlvId ? (
        <div className="grid grid-cols-2 gap-3" style={{ height: "calc(100vh - 190px)", minHeight: 420 }}>
          <div className="min-h-0 overflow-auto">{table}</div>
          <section className="min-h-0 rounded-lg border border-border overflow-hidden bg-background">{panel}</section>
        </div>
      ) : table}
    </div>
  );
}
